import { useCallback, useLayoutEffect, useMemo, useRef } from 'react';
import Header from './Header';
import { mapNamesToWidths } from '../utils/mapNamesToWidths';
import TableBody from './TableBody';
import { getTableMinWidth, makeGetFrozenOffsets, setAllWidths } from '../../resizing/redux/widthsSlice';
import { ProvideObserver } from '../../virtualization/context/observerContext';
import { useTableDispatch, useTableSelector } from '../../redux/reduxHooks';
import SelectAllCheckboxHeader from '../../selection/components/SelectAllCheckboxHeader';
import TableTopBar from './TableTopBar';
import { SELECTION_COLUMN_WIDTH, TABLE_PAGINATOR_HEIGHT, TABLE_TOP_BAR_HEIGHT } from '../constants/sizes';
import { Box } from 'foundations-components/transitional/components/Box';
import Paginator from '../../pagination/components/Paginator';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { TableScrollContainer, TableBox, NoFirstDivChildPadding } from './StyledTableComponents';
import { getColumnKey } from '../utils/getColumnKey';
import { useWatchTableContainer } from '../hooks/useWatchTableContainer';
import { getSelectedRowIds, getShouldShowBulkActionsContainer } from '../../selection/redux/selectionSlice';
import SelectQueryRow from '../../selection/components/SelectQueryRow';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const Table = ({
  disableScrollReset,
  getRowId,
  rows,
  columns,
  sorts,
  selectable,
  pageable: showPagination,
  loading,
  pageIndex,
  pageSize,
  pageSizes,
  totalRowCount,
  BulkActionsComponent,
  RowComponent,
  TableComponent,
  headerLeftSlot,
  alternateHeaderLeftSlot,
  headerRightSlot,
  noRowsSlot,
  onColumnWidthChange,
  onColumnOrderChange,
  onPageIndexChange,
  onPageSizeChange,
  onSortsChange,
  canSelectAllRecords,
  numberOfFrozenColumns,
  CustomSelectAllComponent,
  zebraStripe
}) => {
  const rowIds = useMemo(() => rows.map(getRowId), [getRowId, rows]);
  const {
    observer,
    handleTableContainerRef
  } = useWatchTableContainer(rowIds, disableScrollReset);
  const initializedWidths = useRef(false);
  const dispatch = useTableDispatch();
  useLayoutEffect(() => {
    // We only need to set columns widths if the columns changed
    if (!initializedWidths.current) {
      initializedWidths.current = true;
      return;
    }
    dispatch(setAllWidths(mapNamesToWidths(columns)));
  }, [columns, dispatch]);
  const sortMap = useMemo(() => sorts.reduce((acc, {
    columnNamespace,
    columnName,
    direction
  }) => {
    acc[getColumnKey(columnNamespace, columnName)] = direction;
    return acc;
  }, {}), [sorts]);
  const tableMinWidth = useTableSelector(getTableMinWidth) + (selectable ? SELECTION_COLUMN_WIDTH : 0);
  const tableStyle = useMemo(() => ({
    minWidth: tableMinWidth
  }), [tableMinWidth]);

  // TODO: Add multisort when the index page can handle it
  const handleSortChange = useCallback(({
    columnNamespace,
    columnName,
    direction
  }) => {
    onSortsChange({
      nextSorts: [{
        columnNamespace,
        columnName,
        direction
      }]
    });
  }, [onSortsChange]);
  const selectedRowIds = useTableSelector(getSelectedRowIds);
  const isCurrentPageSelected = useMemo(() => rowIds.length > 0 && rowIds.every(rowId => selectedRowIds.has(rowId)), [rowIds, selectedRowIds]);
  const hasMorePages = totalRowCount > rowIds.length;
  const showSelectAllRow = canSelectAllRecords && isCurrentPageSelected && hasMorePages;
  const showTopBar = Boolean(useTableSelector(getShouldShowBulkActionsContainer) || headerLeftSlot || headerRightSlot);
  const requiredTableProps = useMemo(() => ({
    // HACK: This is required for compat with onboarding tours. Once
    // we have renamed the contact "name" column, we can PR their library
    // to use the actual external id selector and remove this one.
    'data-onboarding': 'grid-table',
    'data-table-external-id': 'framework-data-table',
    'data-test-id': 'framework-data-table',
    'data-test-loading': loading,
    style: tableStyle
  }), [loading, tableStyle]);
  const getFrozenOffsets = useMemo(() => {
    if (numberOfFrozenColumns === 0) {
      return () => [];
    }
    const columnKeys = columns.map(column => {
      return getColumnKey(column.columnNamespace, column.columnName);
    }).slice(0, numberOfFrozenColumns - 1);
    return makeGetFrozenOffsets(columnKeys, selectable);
  }, [columns, numberOfFrozenColumns, selectable]);
  const frozenOffsets = useTableSelector(getFrozenOffsets);
  return /*#__PURE__*/_jsxs(Flex, {
    blockSize: "100%",
    "data-test-id": "framework-data-table-container",
    flexDirection: "column",
    inlineSize: "100%",
    overflow: "hidden",
    children: [showTopBar && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_TOP_BAR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(TableTopBar, {
        headerLeftSlot: headerLeftSlot,
        headerRightSlot: headerRightSlot,
        alternateHeaderLeftSlot: alternateHeaderLeftSlot,
        BulkActionsComponent: BulkActionsComponent,
        totalRowCount: totalRowCount
      })
    }), /*#__PURE__*/_jsxs(TableBox, {
      showPagination: showPagination,
      showTopBar: showTopBar,
      loading: loading,
      children: [loading && /*#__PURE__*/_jsx(NoFirstDivChildPadding, {
        children: /*#__PURE__*/_jsx(LoadingOverlay, {
          "data-test-id": "table-loading",
          contextual: true
        })
      }), rows.length > 0 ? /*#__PURE__*/_jsx(TableScrollContainer, {
        ref: handleTableContainerRef,
        children: /*#__PURE__*/_jsx(ProvideObserver, {
          value: observer,
          children: /*#__PURE__*/_jsxs(TableComponent, {
            numberOfFrozenColumns: numberOfFrozenColumns,
            requiredTableProps: requiredTableProps,
            zebraStripe: zebraStripe,
            children: [/*#__PURE__*/_jsxs("thead", {
              children: [/*#__PURE__*/_jsxs("tr", {
                children: [selectable && /*#__PURE__*/_jsx(SelectAllCheckboxHeader, {
                  numberOfFrozenColumns: numberOfFrozenColumns,
                  rowIds: rowIds
                }), columns.map((column, index) => {
                  const key = getColumnKey(column.columnNamespace, column.columnName);
                  return /*#__PURE__*/_jsx(Header, {
                    column: column,
                    columnIndex: index,
                    columnPosition: frozenOffsets[index],
                    isLastColumn: index === columns.length - 1,
                    sortDirection: sortMap[key],
                    onSortChange: handleSortChange,
                    onColumnWidthChange: onColumnWidthChange,
                    onColumnOrderChange: onColumnOrderChange,
                    numberOfFrozenColumns: numberOfFrozenColumns
                  }, key);
                })]
              }), showSelectAllRow && /*#__PURE__*/_jsx(SelectQueryRow, {
                rowIds: rowIds,
                totalRowCount: totalRowCount,
                columnCount: columns.length + (selectable ? 1 : 0),
                CustomSelectAllComponent: CustomSelectAllComponent
              })]
            }), /*#__PURE__*/_jsx(TableBody, {
              columns: columns,
              getRowId: getRowId,
              frozenColumnOffsets: frozenOffsets,
              numberOfFrozenColumns: numberOfFrozenColumns,
              RowComponent: RowComponent,
              rows: rows,
              selectable: selectable
            })]
          })
        })
      }) : noRowsSlot]
    }), showPagination && /*#__PURE__*/_jsx(Box, {
      shrink: 0,
      basis: TABLE_PAGINATOR_HEIGHT,
      inlineSize: "100%",
      children: /*#__PURE__*/_jsx(Paginator, {
        pageIndex: pageIndex,
        pageSize: pageSize,
        pageSizes: pageSizes,
        totalRowCount: totalRowCount,
        onPageIndexChange: onPageIndexChange,
        onPageSizeChange: onPageSizeChange
      })
    })]
  });
};
export default Table;